import { isAxiosError } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";

import { getDB } from "@/db";
import { useElementMutations } from "@/hooks/queries/useElementMutations";
import { useGetElements } from "@/hooks/queries/useGetElements";
import { useGetProfiles } from "@/hooks/queries/useGetProfiles";
import { useUser } from "@/hooks/useUserProvider";
import { captureEvent, setAnalyticsGroups } from "@/lib/analytics";
import { trackFirstWidgetInteraction } from "@/lib/onboarding";
import { LOCALSTORAGE_KEY } from "@/lib/constants";
import { ElementsContext } from "@/lib/context";
import { GridOne } from "@/lib/templates";
import { parseElements } from "@/lib/utils";
import { useAppDispatch } from "@/store/hooks";
import {
  addProfileRequests,
  removeProfileRequests,
} from "@/store/slices/requestsSlice";
import { Elements } from "../elements/Elements";
import { Element } from "../elements/UIElement";
import { CreateDefaultProfileModal } from "../Profile/CreateDefaultProfileModal";
import { ScreenSpinner } from "../ui/spinner";

export const ElementsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const {
    user,
    accessToken,
    isCreateProfileModalOpen,
    setCreateProfileModalOpen,
  } = useUser();

  const [isSwitchingProfiles, setIsSwitchingProfiles] = useState(false);
  // Boot fast path: trust the stored profile id so /profile/:id/elements can
  // start in parallel with /profile; the profiles effect below falls back to
  // profiles[0] (and rewrites the stored id) if the stored id turns out stale.
  const [activeProfileId, setActiveProfileId] = useState<string | null>(
    () => localStorage.getItem("unideck.activeProfileId") || null,
  );
  const [localElements, setLocalElements] = useState<Record<string, Element>>(
    {},
  );
  const [isLocalElementsReady, setIsLocalElementsReady] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  const dispatch = useAppDispatch();

  const {
    syncElements: syncElementsMutation,
    createElement: createElementMutation,
    updateElement: updateElementMutation,
    deleteElement: deleteElementMutation,
    hasUnsyncedChanges,
    clearUnsyncedChanges,
  } = useElementMutations({
    profileId: activeProfileId,
  });

  const {
    data: profiles,
    isPending: isPendingProfiles,
    refetch: fetchProfiles,
  } = useGetProfiles({
    enabled: !!accessToken,
  });
  const { data: userElements, isFetching } = useGetElements({
    profiles: profiles || [],
    profileId: activeProfileId || undefined,
    enabled: !!accessToken && !!activeProfileId,
  });

  const updateActiveProfileId = useCallback(
    async (id: string | null) => {
      const previousId = activeProfileId;
      if (id !== activeProfileId) {
        setIsSwitchingProfiles(true);
      }

      if (activeProfileId) {
        dispatch(removeProfileRequests({ profileId: activeProfileId }));
      }

      setActiveProfileId(id);
      clearUnsyncedChanges();
      localStorage.setItem("unideck.activeProfileId", id || "");

      captureEvent("dashboard_profile_switched", {
        fromProfileId: previousId,
        toProfileId: id,
      });
    },
    [activeProfileId, clearUnsyncedChanges, dispatch],
  );

  const elements = useMemo(() => {
    if (!accessToken) {
      return localElements;
    }

    return parseElements(userElements?.elements || []);
  }, [accessToken, localElements, userElements?.elements]);

  const setElements = useCallback(
    (nextElements: Record<string, Element>) => {
      if (!accessToken || !activeProfileId) {
        setLocalElements(nextElements);
        return;
      }
    },
    [accessToken, activeProfileId],
  );

  const saveElements = useCallback(
    async (
      nextElements?: Record<string, Element>,
      { ignoreSync = false }: { ignoreSync?: boolean } = {},
    ) => {
      const targetElements = nextElements || elements;
      if (!Object.keys(targetElements).length) {
        return;
      }

      if (!accessToken) {
        const db = await getDB();
        await db.clear("elements");
        const tx = db.transaction("elements", "readwrite");
        await Promise.all([
          ...Object.values(targetElements).map(el =>
            tx.store.add({
              ...el,
              profileId: null,
              config: JSON.stringify(el.config),
            }),
          ),
          tx.done,
        ]);

        setLocalElements(targetElements);
        return;
      }

      if (!activeProfileId) {
        return;
      }

      if (ignoreSync) {
        return;
      }

      await syncElementsMutation(targetElements);
    },
    [accessToken, activeProfileId, elements, syncElementsMutation],
  );

  const appendElement = useCallback(
    async (
      elementId: string,
      elementName: keyof typeof Elements,
      targetId: string,
      config: Record<string, unknown> = {},
      options: {
        source?: "gallery_add_button" | "gallery_drag_drop" | "unknown";
      } = {},
    ) => {
      const elementComponent = Elements[
        elementName
      ] as unknown as React.Component<any> & {
        details: { defaultProps: object };
      };

      const newElement: Element = {
        id: elementId,
        name: elementName,
        parentId: targetId,
        childrenIds: [],
        config: { ...elementComponent.details.defaultProps, ...config },
      };

      const currentElements = parseElements(Object.values(elements));
      if (!currentElements[targetId]) {
        return;
      }

      if (!accessToken) {
        const optimistic = {
          ...currentElements,
          [elementId]: newElement,
          [targetId]: {
            ...currentElements[targetId],
            childrenIds: [
              ...(currentElements[targetId].childrenIds || []),
              elementId,
            ],
          },
        };
        await saveElements(optimistic, { ignoreSync: true });
      } else if (!activeProfileId) {
        return;
      } else {
        await createElementMutation(newElement);
      }

      captureEvent("widget_added", {
        profileId: activeProfileId,
        widgetName: elementName,
        source: options.source || "unknown",
      });
      trackFirstWidgetInteraction("add");
    },
    [
      accessToken,
      activeProfileId,
      elements,
      createElementMutation,
      saveElements,
    ],
  );

  const removeElement = useCallback(
    async (elementId: string) => {
      const currentElements = parseElements(Object.values(elements));
      const element = currentElements[elementId];
      if (!element) {
        return;
      }

      if (element.parentId === null) {
        throw new Error("Cannot remove root element");
      }

      const optimistic = {
        ...currentElements,
        [element.parentId]: {
          ...currentElements[element.parentId],
          childrenIds: (
            currentElements[element.parentId].childrenIds || []
          ).filter(id => id !== elementId),
        },
      };
      delete optimistic[elementId];

      if (!accessToken) {
        await saveElements(optimistic, { ignoreSync: true });
      } else if (!activeProfileId) {
        return;
      } else {
        try {
          await deleteElementMutation(elementId);
        } catch (error) {
          if (isAxiosError(error) && error.response?.status === 404) {
            return;
          }

          throw error;
        }
      }

      captureEvent("widget_removed", {
        profileId: activeProfileId,
        widgetName: element.name,
      });
      trackFirstWidgetInteraction("remove");
    },
    [
      accessToken,
      activeProfileId,
      elements,
      deleteElementMutation,
      saveElements,
    ],
  );

  const updateElement = useCallback(
    async (elementId: string, config: Record<string, unknown>) => {
      const currentElements = parseElements(Object.values(elements));
      if (!currentElements[elementId]) {
        return;
      }

      const optimistic = {
        ...currentElements,
        [elementId]: {
          ...currentElements[elementId],
          config,
        },
      };

      if (!accessToken) {
        await saveElements(optimistic, { ignoreSync: true });
        return;
      }

      if (!activeProfileId) {
        return;
      }

      await updateElementMutation(elementId, config);
      trackFirstWidgetInteraction("configure");
    },
    [
      accessToken,
      activeProfileId,
      elements,
      updateElementMutation,
      saveElements,
    ],
  );

  const syncProfiles = useCallback(async () => {
    if (!accessToken) {
      return;
    }

    const { data: fetchedProfiles, isError } = await fetchProfiles();
    if (isError || !fetchedProfiles?.length) {
      return;
    }

    const storedId = localStorage.getItem("unideck.activeProfileId");
    const resolvedId =
      (storedId &&
        fetchedProfiles.find(profile => profile.id === storedId)?.id) ||
      fetchedProfiles[0].id;

    await updateActiveProfileId(resolvedId);
  }, [accessToken, fetchProfiles, updateActiveProfileId]);

  const activeProfile = useMemo(() => {
    return profiles?.find(p => p.id === activeProfileId);
  }, [profiles, activeProfileId]);

  const handleUpdateGreeting = (value: boolean) => {
    setShowGreeting(value);
    localStorage.setItem(`${LOCALSTORAGE_KEY}show_greeting`, value.toString());
  };

  const isShared = useMemo(
    () => !!activeProfile && !!activeProfile.team,
    [activeProfile],
  );

  const isOwned = useMemo(
    () =>
      !isShared ||
      (user?.ownedTeams || []).some(
        team => activeProfile?.team?.id === team.id,
      ),
    [isShared, user, activeProfile],
  );

  useEffect(() => {
    if (isFetching) {
      return;
    }

    setIsSwitchingProfiles(false);
  }, [isFetching]);

  useEffect(() => {
    if (!accessToken) {
      setActiveProfileId(null);

      let cancelled = false;
      const initializeLocalElements = async () => {
        const db = await getDB();
        const elementCount = await db.count("elements");
        if (cancelled) {
          return;
        }

        if (!elementCount) {
          setLocalElements(GridOne());
          setIsLocalElementsReady(true);
          return;
        }

        const storedElements = await db.getAll("elements");
        if (cancelled) {
          return;
        }

        setLocalElements(
          parseElements(
            storedElements.map(el => ({
              ...el,
              config: JSON.parse(el.config),
            })),
          ),
        );
        setIsLocalElementsReady(true);
      };

      void initializeLocalElements();

      return () => {
        cancelled = true;
      };
    }

    setIsLocalElementsReady(false);
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken || !profiles) {
      return;
    }

    if (!profiles.length) {
      setActiveProfileId(null);
      return;
    }

    const storedId = localStorage.getItem("unideck.activeProfileId");
    const resolvedId =
      (storedId && profiles.find(profile => profile.id === storedId)?.id) ||
      profiles[0].id;

    if (resolvedId !== activeProfileId) {
      void updateActiveProfileId(resolvedId);
    }
  }, [accessToken, profiles, activeProfileId, updateActiveProfileId]);

  useEffect(() => {
    if (!userElements?.requests || !activeProfileId) {
      return;
    }

    dispatch(
      addProfileRequests({
        profileId: activeProfileId,
        requests: userElements.requests,
      }),
    );
  }, [userElements?.requests, activeProfileId, dispatch]);

  useEffect(() => {
    Object.assign(window, {
      readElementConfig: (name: string) => {
        Object.values(elements)
          .filter(el => el.name === name)
          .forEach(el => console.log(el.id, el.config));
      },
    });
  }, [elements]);

  useEffect(() => {
    const storedShowGreeting = localStorage.getItem(
      `${LOCALSTORAGE_KEY}show_greeting`,
    );

    setShowGreeting(storedShowGreeting !== "false");
  }, []);

  useEffect(() => {
    setAnalyticsGroups({
      teamId: activeProfile?.team?.id || null,
      teamName: activeProfile?.team?.name || null,
      dashboardId: activeProfile?.id || null,
      dashboardName: activeProfile?.name || null,
    });
  }, [activeProfile]);

  // Logged-in boot gate: also covers /profile still pending (or resolved but
  // the fallback effect above not yet having validated activeProfileId) —
  // without those terms ElementsContainer flashes "No root element found".
  const hasValidActiveProfile = !!profiles?.some(
    profile => profile.id === activeProfileId,
  );
  const isLoadingElements = !accessToken
    ? !isLocalElementsReady
    : isPendingProfiles ||
      (!!profiles?.length && !hasValidActiveProfile) ||
      (!!activeProfileId && !userElements && isFetching);

  return (
    <ElementsContext.Provider
      value={{
        activeProfile,
        profiles: profiles || [],
        isSwitchingProfiles: isFetching && isSwitchingProfiles,
        syncProfiles,
        activeProfileId,
        setActiveProfileId: updateActiveProfileId,
        elements,
        setElements,
        saveElements,
        appendElement,
        removeElement,
        updateElement,
        isDragging,
        setIsDragging,
        showGreeting,
        setShowGreeting: handleUpdateGreeting,
        isProfileOwned: isOwned,
        isProfileShared: isShared,
        hasUnsyncedChanges,
        isLocked,
        setIsLocked,
      }}
    >
      {isLoadingElements ? (
        <div className="bg-background flex min-h-screen w-screen items-center justify-center">
          <div className="flex flex-col gap-4">
            <ScreenSpinner />
            <span className="text-muted-foreground animate-pulse">
              Fetching dashboard information
            </span>
          </div>
        </div>
      ) : (
        children
      )}
      <CreateDefaultProfileModal
        open={isCreateProfileModalOpen}
        onOpenChange={setCreateProfileModalOpen}
      />
    </ElementsContext.Provider>
  );
};
